import isEqual from 'lodash.isequal';
import { useEffect, useState } from 'react';
import { useAppSelector } from 'redux-state/configure-store.js';
import {
  ILocationWarningState,
  IWarning,
  IWarningSummary,
  WarningLoadingState,
} from 'redux-state/weather-warnings/types.js';
import { PageLocationSummary } from 'types/markerTypes/location.page.js';
import { WarningSeverity, getSeverityInfo, warnLevelHighest } from 'utilities/weather-warnings/warning-severity.js';
import { getCurrentLocationWarnings } from 'utilities/weather-warnings/warnings.js';
import { PanelHeaderConfig, formatForLocationWarning } from './utils/format-for-location-warning.js';
import { IWeatherWarningPanelProps } from './warning-panel.js';

type IWarningsPanelLoading = {
  type: WarningLoadingState.loading;
  highestSeverity: WarningSeverity.None;
  colorClass: '';
};
type IWarningsPanelFailure = {
  type: WarningLoadingState.failed;
  highestSeverity: WarningSeverity.None;
  colorClass: '';
};
export type IWarningsPanelLoadedSummary = {
  type: WarningLoadingState.loaded;
  hasWarnings: true;
  loadedType: LoadedType.NATIONAL;
  highestSeverity: WarningSeverity;
  list: PanelHeaderConfig[];
  colorClass: string;
};
export type IWarningsPanelLoadedWarnings = {
  type: WarningLoadingState.loaded;
  hasWarnings: true;
  loadedType: LoadedType.REGIONAL;
  highestSeverity: WarningSeverity;
  list: PanelHeaderConfig[];
  colorClass: string;
};
export type IWarningsPanelLoadedEmpty = {
  type: WarningLoadingState.loaded;
  hasWarnings: false;
  highestSeverity: WarningSeverity.None;
  colorClass: string;
};

export enum LoadedType {
  // summary
  NATIONAL = 'national',
  // warnings
  REGIONAL = 'regional',
}

export type IWarningsPanelData =
  | IWarningsPanelLoading
  | IWarningsPanelFailure
  | IWarningsPanelLoadedSummary
  | IWarningsPanelLoadedWarnings
  | IWarningsPanelLoadedEmpty;

type GetWarningsProps = {
  loadState: WarningLoadingState | undefined;
  summary?: ReadonlyArray<IWarningSummary>;
  warnings?: ReadonlyArray<IWarning<'JS'>>;
};

/** Return loaded state and summary list or warnings list */
const getWarnings = ({ loadState, summary, warnings }: GetWarningsProps): IWarningsPanelData => {
  if (loadState === WarningLoadingState.failed) {
    return {
      type: WarningLoadingState.failed,
      colorClass: '',
      highestSeverity: WarningSeverity.None,
    };
  }
  if (loadState === WarningLoadingState.loading) {
    return {
      type: WarningLoadingState.loading,
      colorClass: '',
      highestSeverity: WarningSeverity.None,
    };
  }
  if (loadState === WarningLoadingState.loaded) {
    if (summary?.length) {
      const list = formatForLocationWarning({ warnings: summary, type: 'NATIONAL' });
      const highestSeverity = warnLevelHighest(list.map((a) => a.warnLevel));
      const colorClass = getSeverityInfo(highestSeverity).colorClass;
      return {
        list,
        highestSeverity: highestSeverity,
        colorClass,
        hasWarnings: true,
        loadedType: LoadedType.NATIONAL,
        type: WarningLoadingState.loaded,
      };
    } else if (warnings?.length) {
      const list = formatForLocationWarning({ warnings, type: 'REGIONAL' });
      const highestSeverity = warnLevelHighest(list.map((a) => a.warnLevel));
      const colorClass = getSeverityInfo(highestSeverity).colorClass;
      return {
        list,
        highestSeverity,
        colorClass,
        hasWarnings: true,
        loadedType: LoadedType.REGIONAL,
        type: WarningLoadingState.loaded,
      };
    } else {
      // loaded but no warnings
      return {
        highestSeverity: WarningSeverity.None,
        colorClass: '',
        hasWarnings: false,
        type: WarningLoadingState.loaded,
      };
    }
  }
  return {
    highestSeverity: WarningSeverity.None,
    colorClass: '',
    type: WarningLoadingState.failed,
  };
};

const UseWarningsThings = ({
  location,
}: {
  location?: PageLocationSummary;
}): Readonly<ILocationWarningState> | undefined => {
  const [warningsState, setWarningsState] = useState<Readonly<ILocationWarningState>>();
  const warningsSelector = useAppSelector((state) => state.WEATHER_WARNINGS.warnings);
  const _warningsState = getCurrentLocationWarnings(location ?? {}, warningsSelector);
  useEffect(() => {
    if (!isEqual(_warningsState, warningsState)) {
      setWarningsState(_warningsState);
    }
  }, [_warningsState, warningsState]);

  return warningsState;
};

/**
 * Weather warning panel shown at top of page
 * Will always display if warningsFailed
 */
export const WeatherWarningPanelData = ({
  location,
  overrideWarningsData,
}: IWeatherWarningPanelProps): { warningMeta?: ILocationWarningState; warningState?: IWarningsPanelData } => {
  const warningsState = UseWarningsThings({ location });
  if (overrideWarningsData) {
    const { summary, loadState, warnings } = overrideWarningsData;
    const warningState = getWarnings({ loadState, summary, warnings });
    const warningMeta = {
      ...overrideWarningsData,
      warningAreaName: overrideWarningsData.warningAreaName ?? location?.label,
    };
    return {
      warningState,
      warningMeta,
    };
  }
  if (warningsState) {
    const { summary, loadState, warnings } = warningsState;
    const warningState = getWarnings({ loadState, summary, warnings });
    const warningMeta = {
      ...warningsState,
      warningAreaName: warningsState.warningAreaName ?? location?.label,
    };
    return {
      warningState,
      warningMeta,
    };
  }
  return {};
};
